import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PayButton from './Components/PayButton';
import Logo from './images/ecash-logo-horizontal.png';
import BtcIcon from './images/btc-icon.png';
import XecIcon from './images/xec-icon.png';
import SiteLinks from './SiteLinks/SiteLinks';
import 'animate.css';

const DEFAULT_XEC_PRICE_USD = 0.0000054;

const TIER_CONFIG = [
  { key: 'shrimp', label: 'Shrimp', thresholdXec: 1, icon: '/icons/shrimp-icon.png' },
  { key: 'crab', label: 'Crab', thresholdXec: 1000000, icon: '/icons/crab-icon.png' },
  { key: 'piranha', label: 'Piranha', thresholdXec: 10000000, icon: '/icons/piranha-icon.png' },
  { key: 'octopus', label: 'Octopus', thresholdXec: 100000000, icon: '/icons/octopus-icon.png' },
  { key: 'barracuda', label: 'Barracuda', thresholdXec: 500000000, icon: '/icons/barracuda-icon.png' },
  { key: 'swordfish', label: 'Swordfish', thresholdXec: 2000000000, icon: '/icons/swordfish-icon.png' },
  { key: 'shark', label: 'Shark', thresholdXec: 5000000000, icon: '/icons/shark-icon.png' },
  { key: 'orca', label: 'Orca', thresholdXec: 10000000000, icon: '/icons/orca-icon.png' },
  { key: 'whale', label: 'Whale', thresholdXec: 20000000000, icon: '/icons/whale-icon.png' },
];

const ECOSYSTEM_SITES = [
  { name: 'blockchain.poker', url: 'https://blockchain.poker?m=BUYECASH&c=xec', description: 'Poker with eCash' },
  { name: 'firma.cash', url: 'https://firma.cash', description: 'Merchant payments' },
  { name: 'cashtab.com', url: 'https://cashtab.com', description: 'Web wallet' },
  { name: 'agora.cash', url: 'https://agora.cash', description: 'NFT marketplace' },
  { name: 'hodlwars.com', url: 'https://hodlwars.com', description: 'Strategy game' },
  { name: 'blitzchips.com', url: 'https://blitzchips.com', description: 'Gaming' },
  { name: 'everydayjackpot.com', url: 'https://everydayjackpot.com', description: 'Daily jackpot' },
  { name: 'ecash.land', url: 'https://ecash.land', description: 'Ecosystem hub' },
];

const SOCIAL_LINKS = [
  { name: 'Telegram', url: 'https://t.me/ecash_official' },
  { name: 'Twitter', url: 'https://twitter.com/eCashOfficial' },
  { name: 'Discord', url: 'https://discord.gg/ecash' },
];

const isValidXecAddress = (address) => /^ecash:[qp][a-z0-9]{41}$/.test(address);

const getCashtabProviderStatus = () => typeof window.bitcoinAbc !== 'undefined';

const roundSuggestedUsd = (rawUsd) => {
  if (!Number.isFinite(rawUsd) || rawUsd <= 0) {
    return 1;
  }

  if (rawUsd < 25) {
    return Math.max(1, Math.round(rawUsd));
  }

  if (rawUsd < 100) {
    return Math.round(rawUsd / 5) * 5;
  }

  if (rawUsd < 1000) {
    return Math.round(rawUsd / 25) * 25;
  }

  if (rawUsd < 5000) {
    return Math.round(rawUsd / 100) * 100;
  }

  if (rawUsd < 25000) {
    return Math.round(rawUsd / 250) * 250;
  }

  if (rawUsd < 100000) {
    return Math.round(rawUsd / 1000) * 1000;
  }

  return Math.round(rawUsd / 5000) * 5000;
};

const formatUsd = (amount) => `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount)}`;

const formatPriceUsd = (price) =>
  `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  }).format(price)}`;

const formatXec = (amount) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);

const LandingPage = () => {
  const [xecAddress, setXecAddress] = useState('');
  const [selectedUsdAmount, setSelectedUsdAmount] = useState(null);
  const [selectedTierKey, setSelectedTierKey] = useState(null);
  const [customUsdAmount, setCustomUsdAmount] = useState('');
  const [cashtabAvailable, setCashtabAvailable] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [xecPriceUsd, setXecPriceUsd] = useState(null);
  const [priceFetchError, setPriceFetchError] = useState(false);

  const effectivePriceUsd = xecPriceUsd || DEFAULT_XEC_PRICE_USD;

  const tierPresets = useMemo(
    () =>
      TIER_CONFIG.map((tier) => ({
        ...tier,
        suggestedUsd: roundSuggestedUsd(tier.thresholdXec * effectivePriceUsd),
      })),
    [effectivePriceUsd],
  );

  const addressIsValid = isValidXecAddress(xecAddress);
  const parsedCustomAmount = Number(customUsdAmount);
  const effectiveAmount = customUsdAmount
    ? Number.isFinite(parsedCustomAmount)
      ? parsedCustomAmount
      : 0
    : selectedUsdAmount;

  const amountIsValid = Number.isFinite(effectiveAmount) && effectiveAmount > 0;
  const needsAddressAttention = !addressIsValid;
  const hasStep2Selection = selectedTierKey !== null || customUsdAmount !== '';
  const needsStep2Attention = addressIsValid && !hasStep2Selection;
  const isTierMode = customUsdAmount === '';
  const customAmountXec = !isTierMode && amountIsValid ? effectiveAmount / effectivePriceUsd : null;

  const activeTierKey = useMemo(() => {
    if (isTierMode) {
      return selectedTierKey;
    }

    if (!Number.isFinite(customAmountXec) || customAmountXec <= 0) {
      return null;
    }

    let matchedTier = 'shrimp';
    for (const tier of TIER_CONFIG) {
      if (customAmountXec >= tier.thresholdXec) {
        matchedTier = tier.key;
      }
    }

    return matchedTier;
  }, [isTierMode, selectedTierKey, customAmountXec]);

  const showEarnOptions = amountIsValid && effectiveAmount < 10;
  const isOverMaxTx = effectiveAmount > 20000;
  const isTierOverMaxTx = isTierMode && isOverMaxTx;
  const payButtonAmount = isTierOverMaxTx ? 20000 : effectiveAmount;
  const payButtonText = isOverMaxTx
    ? 'Buy eCash Max $20k / tx'
    : `Buy eCash (${formatUsd(effectiveAmount)})`;

  useEffect(() => {
    const detectCashtab = () => {
      setCashtabAvailable(getCashtabProviderStatus());
    };

    const handleMessage = (event) => {
      if (event && event.data && event.data.type === 'FROM_CASHTAB' && event.data.address) {
        setXecAddress(event.data.address);
        setCashtabAvailable(true);
      }
    };

    detectCashtab();
    const detectTimer = setInterval(detectCashtab, 1500);

    window.addEventListener('focus', detectCashtab);
    window.addEventListener('message', handleMessage);

    return () => {
      clearInterval(detectTimer);
      window.removeEventListener('focus', detectCashtab);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    fetch('https://avalanche.cash/api/pricebydate/XEC', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Price API returned ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const parsedPrice = Number(data.Price_in_USD);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
          throw new Error('Invalid eCash price response');
        }
        setXecPriceUsd(parsedPrice);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setPriceFetchError(true);
          console.error('Unable to fetch live eCash price', error);
        }
      });

    return () => controller.abort();
  }, []);

  const requestCashtabAddress = () => {
    if (!cashtabAvailable) {
      window.open('https://cashtab.com', '_blank', 'noopener,noreferrer');
      return;
    }

    window.postMessage(
      {
        type: 'FROM_PAGE',
        text: 'Cashtab',
        addressRequest: true,
      },
      '*',
    );
  };

  return (
    <div className="lp-root">
      <header className="lp-header animate__animated animate__fadeInDown">
        <div className="lp-brand-group">
          <Link to="/">
            <img src={Logo} alt="eCash" className="lp-logo" />
          </Link>
          <div className="lp-header-price" aria-label={`Current eCash price: ${formatPriceUsd(effectivePriceUsd)}`}>
            <span className="lp-header-price-label">1 XEC</span>
            <strong>{formatPriceUsd(effectivePriceUsd)}</strong>
            <span className={`lp-header-price-status ${xecPriceUsd ? 'live' : ''}`}>
              {priceFetchError ? 'Fallback estimate' : xecPriceUsd ? 'Live price' : 'Loading live price'}
            </span>
          </div>
        </div>
        <Link to="/exchanges" className="lp-secondary-link">
          Other ways to buy
        </Link>
      </header>

      <main className="lp-main">
        {!paymentComplete ? (
          <section className="lp-card animate__animated animate__fadeInUp">
            <div className="lp-kicker">
              <img src={BtcIcon} alt="Bitcoin" className="lp-kicker-icon" />
              <span>Bitcoin</span>
              <span className="lp-kicker-arrow">-&gt;</span>
              <img src={XecIcon} alt="eCash" className="lp-kicker-icon" />
              <span>eCash</span>
            </div>
            <h1 className="lp-headline">Join the network Bitcoiners are switching to.</h1>
            <p className="lp-subheadline">
              You already have Bitcoin. Swap it for eCash, the Bitcoin with DeFi & instant finality.
            </p>

            <div className={`lp-section ${needsAddressAttention ? 'lp-address-attention' : ''}`}>
              <div className="lp-label">Step 1: Enter your eCash address</div>
              <div className={`lp-address-actions ${needsAddressAttention ? 'attention' : ''}`}>
                <button
                  type="button"
                  className={`lp-cashtab-btn ${cashtabAvailable ? '' : 'unavailable'}`}
                  onClick={requestCashtabAddress}
                >
                  {cashtabAvailable ? 'Use Cashtab address' : 'Install Cashtab to autofill address'}
                </button>
                <a
                  className="lp-alt-link"
                  href="https://electrumabc.org"
                  target="_blank"
                  rel="noreferrer"
                >
                  Advanced users: Electrum ABC
                </a>
              </div>
              <input
                className={`lp-input ${needsAddressAttention ? 'attention' : ''} ${
                  xecAddress.length > 0 ? (addressIsValid ? 'valid' : 'invalid') : ''
                }`}
                type="text"
                placeholder="ecash:..."
                value={xecAddress}
                onChange={(event) => setXecAddress(event.target.value.trim())}
              />
              {!addressIsValid && (
                <div className="lp-error lp-error-attention">
                  {xecAddress.length > 0
                    ? 'Enter a valid eCash address that starts with ecash:q or ecash:p'
                    : 'Enter your eCash address to continue, or use Cashtab/Electrum above.'}
                </div>
              )}
            </div>

            <div className={`lp-section ${needsStep2Attention ? 'lp-step2-attention' : ''}`}>
              <div className="lp-label">Step 2: Pick your holder tier</div>
              {needsStep2Attention && <div className="lp-error lp-error-attention">Choose a tier or enter a custom USD amount.</div>}

              <div className="lp-tier-grid">
                {tierPresets.map((tier) => {
                  const isSelected = activeTierKey === tier.key;
                  return (
                    <button
                      type="button"
                      key={tier.key}
                      className={`lp-tier-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedUsdAmount(tier.suggestedUsd);
                        setSelectedTierKey(tier.key);
                        setCustomUsdAmount('');
                      }}
                    >
                      <img src={tier.icon} alt={tier.label} className="lp-tier-icon" />
                      <div className="lp-tier-name">{tier.label}</div>
                      <div className="lp-tier-usd">{formatUsd(tier.suggestedUsd)}</div>
                      <div className="lp-tier-threshold">{formatXec(tier.thresholdXec)}+ eCash</div>
                    </button>
                  );
                })}
              </div>

              <div className="lp-custom-wrap">
                <input
                  className={`lp-custom-input ${needsStep2Attention ? 'attention' : ''}`}
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Custom USD amount"
                  value={customUsdAmount}
                  onChange={(event) => {
                    setCustomUsdAmount(event.target.value);
                    setSelectedTierKey(null);
                  }}
                />
              </div>
            </div>

            <div className="lp-section lp-pay-section">
              <div className="lp-label">
                Step 3: {showEarnOptions ? 'Earn eCash through the following community sites' : 'Buy eCash with Bitcoin'}
              </div>
              {showEarnOptions ? (
                <div className="lp-shrimp-note">
                  <div className="lp-shrimp-title">eCash Community Sites with Free Faucets</div>
                  <div className="lp-shrimp-links">
                    <a href="https://blockchain.poker?m=BUYECASH&c=xec" target="_blank" rel="noreferrer">
                      <span>blockchain.poker</span>
                      <span>Play & earn eCash </span>
                    </a>
                    <a href="https://hodlwars.com" target="_blank" rel="noreferrer">
                      <span>hodlwars.com</span>
                      <span>Battle & earn eCash </span>
                    </a>
                  </div>
                </div>
              ) : addressIsValid && amountIsValid ? (
                <div className="lp-paybutton-wrap">
                  <PayButton
                    to={xecAddress}
                    amount={payButtonAmount}
                    currency="USD"
                    size="xl"
                    text={payButtonText}
                    hoverText="Pay with Bitcoin"
                    successText="eCash purchase successful"
                    altpayment="btc"
                    disableAltpayment={false}
                    onSuccess={() => setPaymentComplete(true)}
                  />
                </div>
              ) : (
                <button type="button" className="lp-disabled-pay" disabled>
                  Enter a valid eCash address to buy
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="lp-success-card animate__animated animate__fadeInUp">
            <h2>Welcome to eCash</h2>
            <p>Your eCash is on the way. Explore the ecosystem while you wait.</p>
            <div className="lp-site-grid">
              {ECOSYSTEM_SITES.map((site) => (
                <a key={site.name} href={site.url} target="_blank" rel="noreferrer" className="lp-site-tile">
                  <div className="lp-site-name">{site.name}</div>
                  <div className="lp-site-desc">{site.description}</div>
                </a>
              ))}
            </div>
            <div className="lp-socials">
              {SOCIAL_LINKS.map((social) => (
                <a key={social.name} href={social.url} target="_blank" rel="noreferrer" className="lp-social-link">
                  {social.name}
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteLinks />
    </div>
  );
};

export default LandingPage;








